import * as ko from "knockout";

export class ThemeSwitchExtention {
    /**
     * Initializes the Knockout custom binding handler "themeSwitch".
     * This binding connects a checkbox input to a theme toggle (light/dark).
     * It syncs the checkbox with a Knockout observable<boolean>, applies the theme,
     * and stores the selected mode in localStorage.
     *
     * @example
     * // Initialization
     * ThemeSwitchExtention.Init();
     *
     * // ViewModel
     * class AppViewModel {
     *     darkMode = ko.observable(false);
     * }
     *
     * // Apply Knockout bindings
     * const vm = new AppViewModel();
     * ko.applyBindings(vm);
     *
     * // HTML
     * // <input type="checkbox" data-bind="themeSwitch: darkMode">
     */
    static Init() {
        ko.bindingHandlers.themeSwitch = {
            init: (element, valueAccessor) => {
                const observable = valueAccessor(); // ko.observable<boolean>

                // Read saved theme mode from localStorage
                const savedTheme = localStorage.getItem("themeMode");
                if (savedTheme) {
                    observable(savedTheme === "dark");
                }

                // Apply theme on initialization
                ThemeSwitchExtention.applyTheme(observable());

                // Handle checkbox changes (two-way binding)
                element.addEventListener("change", () => {
                    observable((element as HTMLInputElement).checked);
                });

                // React to observable changes
                observable.subscribe((isDark: boolean) => {
                    ThemeSwitchExtention.applyTheme(isDark);
                    localStorage.setItem("themeMode", isDark ? "dark" : "light");
                    (element as HTMLInputElement).checked = isDark;
                });

                // Set initial checkbox state
                (element as HTMLInputElement).checked = observable();
            }
        };
    }

    /**
     * Applies the theme to the <html> element.
     *
     * @param isDark - true to apply dark theme, false for light theme
     */
    private static applyTheme(isDark: boolean) {
        const html = document.documentElement;
        if (isDark) {
            html.classList.remove("light");
            html.classList.add("dark");
            html.setAttribute("data-sps-theme-switch-mode", "dark");
        } else {
            html.classList.remove("dark");
            html.classList.add("light");
            html.setAttribute("data-sps-theme-switch-mode", "light");
        }
    }
}
